// Browser-compatible SQLite Manager for ACCTOO
// Uses IndexedDB for browser-based database operations

class BrowserSQLiteManager {
  constructor() {
    this.db = null;
    this.dbName = 'ACCTOO_SQLITE_DB';
    this.version = 1;
    this.userId = null;
    this.companyId = null;
    this.deviceId = null;
  }

  // Initialize SQLite for browser
  async init(userId, companyId, deviceId) {
    try {
      this.userId = userId;
      this.companyId = companyId;
      this.deviceId = deviceId;

      console.log('🔄 Initializing browser SQLite...', { userId, companyId, deviceId });

      // Initialize IndexedDB
      await this.createOrLoadDatabase();

      console.log('✅ Browser SQLite initialized successfully');
      return this.db;
    } catch (error) {
      console.error('❌ Browser SQLite initialization failed:', error);
      throw error;
    }
  }

  // Create or load database
  async createOrLoadDatabase() {
    try {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, this.version);

        request.onerror = () => {
          console.error('❌ IndexedDB open failed:', request.error);
          reject(request.error);
        };

        request.onsuccess = () => {
          this.db = request.result;
          console.log('✅ IndexedDB database opened successfully');
          resolve(this.db);
        };

        request.onupgradeneeded = (event) => {
          console.log('🔄 Creating IndexedDB schema...');
          const db = event.target.result;
          this.createStores(db);
          console.log('✅ IndexedDB schema created successfully');
        };
      });
    } catch (error) {
      console.error('❌ Create/load database failed:', error);
      throw error;
    }
  }

  // Create object stores
  createStores(db) {
    // Users table
    if (!db.objectStoreNames.contains('users')) {
      const usersStore = db.createObjectStore('users', { keyPath: 'id' });
      usersStore.createIndex('email', 'email', { unique: true });
    }

    // Companies table
    if (!db.objectStoreNames.contains('companies')) {
      const companiesStore = db.createObjectStore('companies', { keyPath: 'id' });
      companiesStore.createIndex('user_id', 'user_id', { unique: false });
    }

    // Devices table
    if (!db.objectStoreNames.contains('devices')) {
      const devicesStore = db.createObjectStore('devices', { keyPath: 'id' });
      devicesStore.createIndex('device_id', 'device_id', { unique: true });
      devicesStore.createIndex('company_id', 'company_id', { unique: false });
    }

    // Parties table
    if (!db.objectStoreNames.contains('parties')) {
      const partiesStore = db.createObjectStore('parties', { keyPath: 'id' });
      partiesStore.createIndex('company_id', 'company_id', { unique: false });
      partiesStore.createIndex('device_id', 'device_id', { unique: false });
      partiesStore.createIndex('firm_name', 'firm_name', { unique: false });
    }

    // Items table
    if (!db.objectStoreNames.contains('items')) {
      const itemsStore = db.createObjectStore('items', { keyPath: 'id' });
      itemsStore.createIndex('company_id', 'company_id', { unique: false });
      itemsStore.createIndex('device_id', 'device_id', { unique: false });
      itemsStore.createIndex('item_name', 'item_name', { unique: false });
    }

    // Sales Bills table
    if (!db.objectStoreNames.contains('sales_bills')) {
      const salesBillsStore = db.createObjectStore('sales_bills', { keyPath: 'id' });
      salesBillsStore.createIndex('company_id', 'company_id', { unique: false });
      salesBillsStore.createIndex('device_id', 'device_id', { unique: false });
      salesBillsStore.createIndex('bill_number', 'bill_number', { unique: false });
    }

    // Challan table
    if (!db.objectStoreNames.contains('challan')) {
      const challanStore = db.createObjectStore('challan', { keyPath: 'id' });
      challanStore.createIndex('company_id', 'company_id', { unique: false });
      challanStore.createIndex('device_id', 'device_id', { unique: false });
      challanStore.createIndex('challan_number', 'challan_number', { unique: false });
    }

    // Quotation table
    if (!db.objectStoreNames.contains('quotation')) {
      const quotationStore = db.createObjectStore('quotation', { keyPath: 'id' });
      quotationStore.createIndex('company_id', 'company_id', { unique: false });
      quotationStore.createIndex('device_id', 'device_id', { unique: false });
      quotationStore.createIndex('quotation_number', 'quotation_number', { unique: false });
    }

    // Purchase Bills table
    if (!db.objectStoreNames.contains('purchase_bills')) {
      const purchaseBillsStore = db.createObjectStore('purchase_bills', { keyPath: 'id' });
      purchaseBillsStore.createIndex('company_id', 'company_id', { unique: false });
      purchaseBillsStore.createIndex('device_id', 'device_id', { unique: false });
      purchaseBillsStore.createIndex('purchase_number', 'purchase_number', { unique: false });
    }

    // Purchase Orders table
    if (!db.objectStoreNames.contains('purchase_orders')) {
      const purchaseOrdersStore = db.createObjectStore('purchase_orders', { keyPath: 'id' });
      purchaseOrdersStore.createIndex('company_id', 'company_id', { unique: false });
      purchaseOrdersStore.createIndex('device_id', 'device_id', { unique: false });
      purchaseOrdersStore.createIndex('order_number', 'order_number', { unique: false });
    }

    // Payments table
    if (!db.objectStoreNames.contains('payments')) {
      const paymentsStore = db.createObjectStore('payments', { keyPath: 'id' });
      paymentsStore.createIndex('company_id', 'company_id', { unique: false });
      paymentsStore.createIndex('device_id', 'device_id', { unique: false });
      paymentsStore.createIndex('receipt_number', 'receipt_number', { unique: false });
    }

    // Expenses table
    if (!db.objectStoreNames.contains('expenses')) {
      const expensesStore = db.createObjectStore('expenses', { keyPath: 'id' });
      expensesStore.createIndex('company_id', 'company_id', { unique: false });
      expensesStore.createIndex('device_id', 'device_id', { unique: false });
    }

    // Bill sequences table
    if (!db.objectStoreNames.contains('bill_sequences')) {
      const billSequencesStore = db.createObjectStore('bill_sequences', { keyPath: 'id' });
      billSequencesStore.createIndex('company_id', 'company_id', { unique: false });
      billSequencesStore.createIndex('device_id', 'device_id', { unique: false });
      billSequencesStore.createIndex('bill_type', 'bill_type', { unique: false });
    }

    // Sync queue table
    if (!db.objectStoreNames.contains('sync_queue')) {
      const syncQueueStore = db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
      syncQueueStore.createIndex('company_id', 'company_id', { unique: false });
      syncQueueStore.createIndex('device_id', 'device_id', { unique: false });
      syncQueueStore.createIndex('status', 'status', { unique: false });
    }
  }

  // =============================================
  // DATA OPERATIONS
  // =============================================

  // Insert data
  async insert(tableName, data) {
    try {
      const enhancedData = {
        ...data,
        company_id: this.companyId,
        device_id: this.deviceId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([tableName], 'readwrite');
        const store = transaction.objectStore(tableName);
        const request = store.add(enhancedData);

        request.onsuccess = () => {
          console.log(`✅ Record inserted in ${tableName}:`, data.id);
          resolve({ success: true, id: data.id });
        };
        request.onerror = () => {
          console.error(`❌ Insert failed for ${tableName}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`❌ Insert failed for ${tableName}:`, error);
      throw error;
    }
  }

  // Update data
  async update(tableName, id, data) {
    try {
      const enhancedData = {
        ...data,
        updated_at: new Date().toISOString()
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([tableName], 'readwrite');
        const store = transaction.objectStore(tableName);
        
        // First get the existing record
        const getRequest = store.get(id);
        getRequest.onsuccess = () => {
          const existingRecord = getRequest.result;
          if (existingRecord) {
            const updatedRecord = { ...existingRecord, ...enhancedData };
            const putRequest = store.put(updatedRecord);
            
            putRequest.onsuccess = () => {
              console.log(`✅ Record updated in ${tableName}:`, id);
              resolve({ success: true, id });
            };
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            reject(new Error('Record not found'));
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (error) {
      console.error(`❌ Update failed for ${tableName}:`, error);
      throw error;
    }
  }

  // Select data
  async select(tableName, filters = {}) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([tableName], 'readonly');
        const store = transaction.objectStore(tableName);
        const request = store.getAll();

        request.onsuccess = () => {
          let results = request.result;
          
          // Apply filters
          if (filters.company_id) {
            results = results.filter(item => item.company_id === filters.company_id);
          }
          if (filters.device_id) {
            results = results.filter(item => item.device_id === filters.device_id);
          }
          if (filters.id) {
            results = results.filter(item => item.id === filters.id);
          }
          
          console.log(`✅ Records selected from ${tableName}:`, results.length);
          resolve(results);
        };
        request.onerror = () => {
          console.error(`❌ Select failed for ${tableName}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`❌ Select failed for ${tableName}:`, error);
      throw error;
    }
  }

  // Delete data
  async delete(tableName, id) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([tableName], 'readwrite');
        const store = transaction.objectStore(tableName);
        const request = store.delete(id);

        request.onsuccess = () => {
          console.log(`✅ Record deleted from ${tableName}:`, id);
          resolve({ success: true, id });
        };
        request.onerror = () => {
          console.error(`❌ Delete failed for ${tableName}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`❌ Delete failed for ${tableName}:`, error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER GENERATION
  // =============================================

  // Generate bill number
  async generateBillNumber(billType) {
    try {
      const sequenceId = `${this.companyId}_${this.deviceId}_${billType}`;
      const sequences = await this.select('bill_sequences', { id: sequenceId });
      
      let sequence = sequences[0];
      if (!sequence) {
        // Create new sequence
        sequence = {
          id: sequenceId,
          company_id: this.companyId,
          device_id: this.deviceId,
          bill_type: billType,
          prefix: 'INV',
          counter: '',
          last_number: 0,
          current_number: 0
        };
        await this.insert('bill_sequences', sequence);
      }

      // Increment counter
      const newNumber = sequence.current_number + 1;
      await this.update('bill_sequences', sequenceId, {
        current_number: newNumber,
        last_number: newNumber
      });

      // Generate bill number with specific prefixes
      const year = new Date().getFullYear().toString().slice(-2); // Last 2 digits
      const nextYear = (parseInt(year) + 1).toString().slice(-2);
      const paddedNumber = newNumber.toString().padStart(4, '0'); // 4 digits

      let prefix = sequence.prefix;
      let format = '';

      // Use specific prefixes and formats for each bill type
      switch (billType) {
        case 'sales':
          prefix = 'INV';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'purchase':
          prefix = 'PRB';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'purchase_order':
          prefix = 'PRO';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'challan':
          prefix = 'CHA';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'quotation':
          prefix = 'QUO';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'payment_receipt':
          prefix = 'PRI';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'advance':
          prefix = 'ADV-INV';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        case 'purchase_payment':
          prefix = 'PRP';
          format = `${prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
          break;
        default:
          format = `${sequence.prefix}${year}-${nextYear}/${sequence.counter}/${paddedNumber}`;
      }

      console.log('✅ Bill number generated:', format);
      return format;
    } catch (error) {
      console.error('❌ Bill number generation failed:', error);
      throw error;
    }
  }

  // Setup bill sequences
  async setupBillSequences(companyId, deviceId, billCounter = '') {
    try {
      const billTypes = [
        { type: 'sales', prefix: 'INV' },
        { type: 'challan', prefix: 'CH' },
        { type: 'quotation', prefix: 'QT' },
        { type: 'purchase', prefix: 'PO' },
        { type: 'purchase_order', prefix: 'PUR' }
      ];

      for (const billType of billTypes) {
        const sequenceId = `${companyId}_${deviceId}_${billType.type}`;
        const existingSequences = await this.select('bill_sequences', { id: sequenceId });
        
        if (existingSequences.length === 0) {
          await this.insert('bill_sequences', {
            id: sequenceId,
            company_id: companyId,
            device_id: deviceId,
            bill_type: billType.type,
            prefix: billType.prefix,
            counter: billCounter,
            last_number: 0,
            current_number: 0
          });
        }
      }

      console.log('✅ Bill sequences setup for device:', deviceId);
    } catch (error) {
      console.error('❌ Bill sequences setup failed:', error);
      throw error;
    }
  }

  // =============================================
  // SYNC OPERATIONS
  // =============================================

  // Add to sync queue
  async addToSyncQueue(tableName, recordId, operation, data) {
    try {
      await this.insert('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId,
        table_name: tableName,
        record_id: recordId,
        operation: operation,
        data: JSON.stringify(data),
        status: 'pending'
      });

      console.log('✅ Added to sync queue:', { tableName, recordId, operation });
    } catch (error) {
      console.error('❌ Add to sync queue failed:', error);
      throw error;
    }
  }

  // Get pending sync records
  async getPendingSyncRecords() {
    try {
      const records = await this.select('sync_queue', { 
        company_id: this.companyId, 
        device_id: this.deviceId 
      });
      
      const pendingRecords = records.filter(record => record.status === 'pending');
      console.log('✅ Pending sync records:', pendingRecords.length);
      return pendingRecords;
    } catch (error) {
      console.error('❌ Get pending sync records failed:', error);
      throw error;
    }
  }

  // =============================================
  // BACKUP AND RESTORE
  // =============================================

  // Save database to localStorage
  async saveDatabase() {
    try {
      console.log('✅ Database saved to IndexedDB');
      return { success: true };
    } catch (error) {
      console.error('❌ Save database failed:', error);
      throw error;
    }
  }

  // Create backup
  async createBackup() {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const backupKey = `backup_${this.companyId}_${this.deviceId}_${timestamp}`;
      
      console.log('✅ Backup created:', backupKey);
      return { success: true, backupKey };
    } catch (error) {
      console.error('❌ Create backup failed:', error);
      throw error;
    }
  }

  // =============================================
  // STATISTICS
  // =============================================

  // Get database statistics
  async getDatabaseStats() {
    try {
      const tables = [
        'users', 'companies', 'devices', 'parties', 'items',
        'sales_bills', 'challan', 'quotation', 'purchase_bills', 'purchase_orders',
        'payments', 'expenses', 'sync_queue'
      ];

      const stats = {};
      for (const table of tables) {
        const records = await this.select(table, { company_id: this.companyId });
        stats[table] = records.length;
      }

      console.log('✅ Database statistics:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Get database stats failed:', error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Close database
  async close() {
    try {
      if (this.db) {
        this.db.close();
        this.db = null;
        console.log('✅ Database closed');
      }
    } catch (error) {
      console.error('❌ Close database failed:', error);
      throw error;
    }
  }

  // Get database path
  getDatabasePath() {
    return this.dbName;
  }

  // Check if database is connected
  isConnected() {
    return this.db !== null;
  }
}

export default BrowserSQLiteManager;
